import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { usePerfilVista } from "@/hooks/usePerfilVista";

export interface AvatarVista {
  nombre: string;
  fotoUrl: string | null;
  /** En línea (RPC `presencia_usuario`); `null` si no se puede saber (sin RPC o sin acceso): sin punto. */
  enLinea: boolean | null;
  /** Última actividad (ISO) para el tooltip; `null` si no hay. */
  ultimaActividad: string | null;
  /** Con "Ver como": todavía se lee la foto y el estado de la persona vista. */
  cargando: boolean;
}

/**
 * Lo que pinta el avatar del encabezado y del pie del menú: foto, estado y nombre de la identidad
 * EFECTIVA (la persona vista con "Ver como", si no la sesión). Una sola lectura de `usuarios`
 * para la foto, para que el avatar nunca quede vacío mientras llega: `cargando` pide
 * esqueleto y, sin foto (o si falla), `AvatarPersona` cae a las iniciales.
 */
export function useAvatarVista(): AvatarVista {
  const { profile } = useAuth();
  const perfil = usePerfilVista();
  const correo = perfil.email.trim().toLowerCase();
  const { data, isLoading } = useQuery({
    queryKey: ["avatar_vista", correo],
    enabled: perfil.impersonando && !!correo,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data: fila } = await supabase
        .from("usuarios")
        .select("foto_perfil_url")
        .eq("email", correo)
        .maybeSingle();
      return (fila ?? null) as { foto_perfil_url: string | null } | null;
    },
  });
  // Presencia de la identidad efectiva (propia o vista), cada 60 s mientras el avatar está montado.
  const correoPresencia = (perfil.impersonando ? correo : (profile?.email ?? "").trim().toLowerCase());
  const { data: presencia } = useQuery({
    queryKey: ["presencia_usuario", correoPresencia],
    enabled: !!correoPresencia,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<{ en_linea: boolean; ultima_actividad: string | null } | null> => {
      // Sin la RPC (PGRST202/42883) o sin acceso (42501) no hay dato: sin punto y sin avisos.
      const { data: d, error } = await (supabase as any).rpc("presencia_usuario", { p_email: correoPresencia });
      if (error || !d) return null;
      return d as { en_linea: boolean; ultima_actividad: string | null };
    },
  });
  // Sesión propia: si la RPC falla, en línea como antes (estás usando la app).
  const enLinea = presencia ? presencia.en_linea === true : perfil.impersonando ? null : true;
  const ultimaActividad = presencia?.ultima_actividad ?? null;
  if (!perfil.impersonando) {
    return { nombre: perfil.nombre, fotoUrl: profile?.foto_perfil_url ?? null, enLinea, ultimaActividad, cargando: false };
  }
  return { nombre: perfil.nombre, fotoUrl: data?.foto_perfil_url ?? null, enLinea, ultimaActividad, cargando: isLoading };
}
